import { loadConfig } from './config.js';
import { clearSession, currentSession } from './auth.js';

/**
 * Le client d'API.
 *
 * **C'est l'access token qui part**, jamais l'ID token : l'API impose
 * `token_use == "access"` et refuserait l'autre en `401` (api.md §1).
 *
 * Une seule fonction d'appel, et toutes les routes en dérivent : la gestion du
 * `401` — celle qui décide qu'on redescend à l'accueil plutôt que d'insister — n'a
 * ainsi qu'un seul endroit où être juste.
 */

export class Unauthenticated extends Error {}

/** Une erreur métier de l'API, avec son code : le client en aiguille certaines. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: Record<string, unknown>,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

interface CallOptions {
  readonly method?: string;
  readonly body?: unknown;
}

async function call<T>(path: string, opts: CallOptions = {}): Promise<T> {
  const cfg = await loadConfig();
  const session = await currentSession();
  if (!session) throw new Unauthenticated('aucune session');

  const headers: Record<string, string> = {
    accept: 'application/json',
    authorization: `Bearer ${session.accessToken}`,
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(`${cfg.apiUrl}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const code = String(body['code'] ?? 'unknown');

    if (res.status === 401) {
      // **Tous les 401 ne se valent pas.** `sudo_required` dit « votre session
      // est bonne, mais cet acte demande une authentification récente » (D21) :
      // détruire la session ici renverrait à l'accueil quelqu'un de parfaitement
      // connecté, et le geste suivant serait de se reconnecter — soit exactement
      // ce que la réponse demandait, au prix de tout le contexte perdu.
      if (code === 'sudo_required') throw new ApiError(res.status, code, body);

      // Le jeton a été révoqué côté serveur, ou le compte désactivé. Insister
      // ferait boucler ; on redescend à l'écran d'accueil.
      clearSession();
      throw new Unauthenticated('jeton refusé');
    }
    throw new ApiError(res.status, code, body);
  }
  return (await res.json()) as T;
}

/**
 * `GET /me`. Attention à la casse : les attributs **calculés à la réponse** sont
 * en `lowerCamel`, ceux qui viennent d'un item seraient en `PascalCase`
 * (api.md §0). Tout ce que porte `/me` est calculé.
 */
export interface Me {
  readonly uid: string;
  readonly alias?: string;
  /**
   * **La révision de l'avatar**, et non son URL. `0` ⇒ pas d'avatar.
   *
   * C'est elle qui casse les caches : une URL présignée d'avatar vaut 15 minutes,
   * l'image derrière peut changer, et sans un numéro qui bouge chaque membre de
   * chaque groupe garderait l'ancienne — pour toujours.
   */
  readonly avatarRev?: number;
  readonly isAdmin: boolean;
  readonly serverTime: string;
  readonly groups: readonly { gid: string; name: string; role: string }[];
  readonly quotas: Record<string, number>;
  /**
   * Les plafonds **servis**, jamais recopiés. La V1 portait le plafond de partage
   * à 5 dans l'app : un pré-contrôle d'UI qui diverge du serveur produit soit un
   * refus inexplicable, soit une saisie perdue à l'envoi.
   */
  readonly limits: Record<string, number>;
  /**
   * **Les bornes des champs** — `maxTags`, `maxTagBytes`, `maxDescriptionBytes`…
   * Servies par `/me` depuis l'étape 1, lues depuis R6 par l'éditeur de mots-clés.
   */
  readonly fields?: Record<string, number>;
  /**
   * **Combien de temps une suppression reste réversible** — `trashDays` et
   * `tombstoneDays`, servis par `GET /me` depuis l'étape 1 et **non déclarés ici**
   * jusqu'au 2026-09-12.
   *
   * C'est la même règle que les plafonds : un chiffre de rétention codé en dur
   * dans le client finit par contredire le serveur. Trois phrases d'écran
   * l'annonçaient en clair — « 30 jours » —, et deux autres disaient de la même
   * suppression qu'elle était « irrécupérable ». La valeur servie tranche.
   */
  readonly retention?: Record<string, number>;
  readonly storage: { quotaBytes: number; usedBytes: number; deletedBytes: number };
  /**
   * **Ce que le stage offre** *(R6)*. `describe` coupé, « Régénérer » ne paraît
   * pas : le serveur rendrait `503 describe_disabled` à chaque appui.
   */
  readonly features?: { readonly describe?: boolean };
}

export interface Group {
  readonly gid: string;
  readonly name: string;
  readonly role: string;
  readonly memberCount: number;
  readonly createdAt: string;
}

/**
 * Un rôle produit, tel qu'il vit dans `Variants`.
 *
 * `frames` est la seule chose que la vidéo ajoute au modèle *(M5)* : la vignette
 * d'une vidéo **est une pellicule**, 8 cases de 400 px sur une image de
 * 3 200 × 400. Absent ⇒ image fixe, et les photos ne changent donc pas d'un
 * octet. Un client qui ignorerait l'attribut dégraderait proprement — il
 * montrerait la planche entière, écrasée.
 *
 * `skipped` dit qu'un `standard` **n'arrivera jamais** *(M7)* : la vidéo était
 * hors de l'enveloppe de travail de W7, elle garde sa pellicule et sa source, et
 * l'entité est `READY` quand même. Ce n'est pas « pas encore » — c'est « pas ».
 */
export interface Variant {
  readonly ext: string;
  readonly mime: string;
  readonly size?: number;
  readonly width?: number;
  readonly height?: number;
  readonly frames?: number;
  readonly skipped?: boolean;
}

/**
 * Une entité telle que l'API la rend : **`PascalCase`**, parce que ces
 * attributs-là viennent de l'item et que le sérialiseur les recopie (api.md §0).
 *
 * Un album en est une aussi (§5.1) : il porte `Aid` là où une entité porte `Fid`,
 * et c'est la seule façon de les distinguer.
 */
export interface Entity {
  readonly Fid?: string;
  readonly Aid?: string;
  readonly Type: string;
  readonly Kind?: string;
  readonly Name: string;
  readonly State?: string;
  /**
   * **Pourquoi un `FAILED` a échoué** — servi par l'API depuis l'étape 1
   * (`serialize.ts`), et **lu par personne** jusqu'au 2026-09-12.
   *
   * C'est la moitié manquante de l'incident des 42 photos : le serveur nommait la
   * cause, l'écran affichait « échec du traitement » et s'arrêtait là. Un motif
   * inconnu du client se rend **tel quel** plutôt que d'être avalé — un code brut
   * est rapportable, « échec » ne l'est pas.
   */
  readonly FailReason?: string;
  readonly Folder?: string;
  readonly ItemCount?: number;
  /**
   * **Qui peut verser dans une collection** — `owner` ou `members`.
   *
   * `owner` est le défaut du serveur. `members` ouvre la collection aux membres
   * des groupes auxquels elle est partagée : chacun y ajoute **ses** objets, et
   * n'en retire que les siens. C'est le seul endroit du socle où quelqu'un
   * d'autre que le propriétaire écrit dans une collection.
   */
  readonly ContribPolicy?: string;
  /**
   * **Puis-je écrire le contenu de cette entité ?** — rendu par `GET /files/{fid}`
   * **seul**, et absent partout ailleurs.
   *
   * Un client ne peut pas le calculer : `Albums` ne sort jamais vers un tiers, et
   * l'arête d'un feed ne dit rien du partage qui l'a produite. C'est donc le
   * serveur qui tranche, et `undefined` ne veut pas dire « non » — il veut dire
   * « on ne l'a pas encore demandé » (`lowerCamel` : une vue calculée à la
   * réponse, jamais un attribut de l'item).
   */
  readonly canWriteContent?: boolean;
  /**
   * **Les partages où cet objet est rangé** — servis au **propriétaire seul**
   * (`serialize.ts` : un tiers reçoit une liste vide, jamais celle du
   * propriétaire), et **lus par personne** jusqu'au 2026-09-12.
   *
   * C'est le **troisième** champ en trois incréments que le serveur envoie et que
   * le client ignorait, après `FailReason` *(R1)* et `retention` *(R2)*. Celui-ci
   * rendait impossible la seule question qu'on se pose dans un dossier dont on
   * partage une partie — *qu'est-ce qui circule, et qu'est-ce qui ne circule
   * pas* — alors que la réponse voyageait déjà dans chaque réponse.
   *
   * `lowerCamel` : c'est une **vue** calculée à la réponse (l'intersection avec ce
   * que l'appelant peut lire), pas l'attribut `Albums` de l'item.
   *
   * ⚠ Il ne voyage **pas sur une arête** : la projection ne l'a jamais porté
   * (D28, I-27). Dans un partage, le contenu vient d'arêtes — donc `albums` y est
   * toujours absent, et c'est `ownerAlias` qui y répond à l'autre question.
   */
  readonly albums?: readonly string[];
  readonly ContentRev?: number;
  readonly Description?: string | null;
  readonly CreatedAt: string;
  readonly UpdatedAt: string;
  readonly DocumentDate?: string;
  readonly Camera?: string;
  /**
   * **La position de prise de vue** — servie au **propriétaire seul**
   * (`OWNER_FIELDS`), et jamais projetée sur une arête : personne à qui l'objet est
   * partagé ne la reçoit.
   *
   * Elle venait de l'app, qui la lit de l'EXIF et l'envoie ; depuis **R4**
   * *(2026-09-12)* le worker la remplit aussi pour ce qui part du site. Le site ne
   * l'affichait nulle part — c'est le manque que `surfaces.spec.ts` a trouvé le
   * jour même —, et la fiche de détail le comble.
   *
   * Les deux vont ensemble : une latitude seule ne désigne rien, et le serveur ne
   * les écrit jamais l'une sans l'autre.
   */
  readonly GpsLat?: number;
  readonly GpsLon?: number;
  readonly Tags?: readonly string[];
  /**
   * **La trace de la description automatique** *(R5, 2026-09-13)* — servie au
   * **propriétaire seul** (`OWNER_FIELDS`), jamais projetée.
   *
   * `Machine` dit quels champs la machine **tient encore** : c'est ce qui porte la
   * mention « décrit automatiquement » dans la fiche, et ce qui dit à « Régénérer »
   * s'il va écraser une correction — auquel cas il demande confirmation (Q3).
   * Corriger un champ l'en retire, côté serveur.
   */
  readonly Described?: DescribedMark;
  /**
   * **L'empreinte SHA-256 de la source** *(K1)*, en hexadécimal — servie au
   * propriétaire seul. C'est elle que l'import compare pour écarter les doublons
   * exacts (`duplicates.ts`). Absente d'un fichier envoyé avant K1 ou trop gros
   * pour être haché.
   */
  readonly Sha256?: string;
  /**
   * **L'empreinte visuelle** *(K3)* — un pHash de 64 bits de la vignette, en 16
   * chiffres hexadécimaux, servi au propriétaire seul. Le client compare les
   * empreintes de son miroir au seuil choisi pour signaler les doublons probables.
   * Absente d'une vidéo, et d'une photo pas encore rattrapée.
   */
  readonly VisualHash?: string;
  readonly Owner?: string;
  /**
   * Les groupes qui autorisent la lecture, **filtrés au point de vue de
   * l'appelant** : un membre n'y voit que ceux qu'il partage avec le
   * propriétaire, jamais les autres cercles de celui-ci. `lowerCamel` — c'est une
   * vue calculée à la réponse, pas l'attribut `AuthGroups` de l'item.
   */
  readonly authGroups?: readonly string[];
  /**
   * **L'extrait** d'une note — ses premières lignes, **en Markdown**, coupées par
   * le serveur sur une frontière de ligne (`api.md` §0). Ce n'est pas un rendu :
   * le client rend, avec le même moteur que le lecteur. `PascalCase` : c'est un
   * attribut de l'item, pas une vue calculée à la réponse, et il voyage donc
   * aussi sur les arêtes.
   */
  readonly Excerpt?: string;
  readonly Variants?: Record<string, Variant>;
  /**
   * **L'affiche d'une vidéo** (M6) — laquelle des cases de la pellicule la
   * représente. `null` est le retour au défaut, et se lit comme l'absence : c'est
   * la valeur qu'écrit un effacement, l'attribut n'étant jamais retiré de l'item.
   */
  readonly PosterFrame?: number | null;
  /**
   * Les réactions, **agrégées par le serveur à la réponse** (D24).
   *
   * `lowerCamel`, et la casse dit pourquoi : ce n'est pas un attribut de l'item,
   * c'est une **vue** (api.md §0). La map nominative `Reactions` ne sort jamais —
   * elle nommerait à tous les membres de tous les groupes des réacteurs d'autres
   * groupes. Ce qui traverse est un décompte anonyme, plus **sa propre entrée**.
   *
   * `scoreAvg` est calculé à la réponse et jamais stocké : un agrégat en base
   * serait un compteur de plus à réparer.
   */
  readonly reactions?: ReactionAggregate;
}

export interface DescribedMark {
  /** La version du prompt, `desc-v3` au départ. */
  readonly Prompt: string;
  readonly Model: string;
  readonly At: string;
  /** Les champs encore tenus par la machine — `Description`, `Tags`, ou les deux. */
  readonly Machine?: readonly string[];
}

export interface MyReaction {
  readonly emoji?: string;
  readonly score?: number;
}

export interface ReactionAggregate {
  /** `emoji → nombre de personnes`, tous réacteurs confondus. */
  readonly emoji: Record<string, number>;
  readonly scoreCount: number;
  readonly scoreAvg: number | null;
  /** **Sa** propre entrée, ou `null`. La seule qu'on puisse écrire. */
  readonly mine: MyReaction | null;
}

/** L'identifiant d'une entité, quelle que soit sa nature. */
export const idOf = (e: Entity): string => e.Fid ?? e.Aid ?? '';

export interface Folder {
  readonly folder: string;
  readonly itemCount: number;
  readonly updatedAt: string;
}

export interface Page<T> {
  readonly items: T[];
  readonly next: string | null;
}

export const getMe = () => call<Me>('/me');

/**
 * `GET /config` — les **règles du jeu**, servies plutôt que codées en dur.
 *
 * La liste blanche d'emoji en fait partie : l'enrichir ne doit pas demander une
 * version d'app, et un client qui proposerait un emoji absent de la liste ferait
 * refuser la réaction en `400` après le clic. La V1 portait le plafond de partage
 * à 5 dans l'app, et l'a payé exactement ainsi.
 *
 * Attention : c'est la route d'API, pas le `/config.json` déposé par le
 * déploiement — celui-là porte l'amorçage (URL, pool), celle-ci les règles.
 */
export interface ApiConfig {
  readonly emoji: readonly string[];
  readonly limits: Record<string, number>;
}

export const getApiConfig = () => call<ApiConfig>('/config');

/**
 * `GET /groups`. `next` y vaut **toujours `null`** — D29 borne l'effectif et la
 * partition est épuisée (I-30) —, donc aucune pagination à dérouler ici.
 */
export const getGroups = () => call<Page<Group>>('/groups');

/** Le `gid` est **fourni par le client** (D14), comme le `fid` et l'`aid`. */
export const createGroup = (gid: string, name: string) =>
  call<Group>('/groups', { method: 'POST', body: { gid, name } });

export const patchGroup = (gid: string, name: string) =>
  call<{ gid: string; name: string }>(`/groups/${gid}`, { method: 'PATCH', body: { name } });

/**
 * `DELETE /groups/{gid}` — **définitif, et sans corbeille**. Le groupe et toutes
 * ses appartenances sont tombstonés dans une seule transaction : chaque membre en
 * sort à l'instant même. Rien ne le restaure.
 */
export const deleteGroup = (gid: string) =>
  call<{ gid: string; deleted: true }>(`/groups/${gid}`, { method: 'DELETE' });

/**
 * Un membre, **nommé**. `alias` est `null` pour qui n'en a pas choisi : le client
 * décide alors comment l'écrire, et le serveur ne déguise pas un ULID en nom.
 */
export interface Member {
  readonly uid: string;
  readonly alias: string | null;
  /** `0` ⇒ pas d'avatar. Servi avec la liste : sinon il faudrait un appel par membre pour l'apprendre. */
  readonly avatarRev?: number;
  readonly role: string;
  readonly since: string;
}

export const listMembers = (gid: string) => call<Page<Member>>(`/groups/${gid}/members`);

// ─────────────────────────────────────────────────────────────────────────────
// Le profil (api.md §7) — se nommer, et se donner un visage
// ─────────────────────────────────────────────────────────────────────────────

export interface PublicProfile {
  readonly uid: string;
  readonly alias: string | null;
  readonly avatarRev: number;
  readonly hasAvatar: boolean;
}

/**
 * `PUT /profile { alias }`. **`null` retire l'alias** au lieu d'écrire une chaîne
 * vide : le serveur rend l'absence par l'absence, et une chaîne vide y passerait
 * pour un nom — un nom invisible, qu'aucun écran ne saurait remplacer par « Sans
 * nom ».
 */
export const putProfile = (alias: string | null) =>
  call<PublicProfile>('/profile', { method: 'PUT', body: { alias } });

/**
 * `POST /profile/avatar { mime }` — l'URL d'envoi et sa révision.
 *
 * Le protocole est celui des octets (§3), en deux temps plutôt qu'en trois :
 * l'avatar n'a pas d'entité à créer, il a une clé unique par personne.
 */
export const startAvatarUpload = (mime: string) =>
  call<{ url: string; headers: Record<string, string>; expiresIn: number; rev: number; maxBytes: number }>(
    '/profile/avatar',
    { method: 'POST', body: { mime } },
  );

/** `POST /profile/avatar/complete { rev }` — **le serveur constate** (I-16). */
export const completeAvatarUpload = (rev: number) =>
  call<PublicProfile & { bytes: number }>('/profile/avatar/complete', {
    method: 'POST',
    body: { rev },
  });

/**
 * `GET /users/{uid}/avatar/url` — les octets, par URL présignée.
 *
 * Gardée par « un groupe partagé avec l'appelant », comme le profil : rendre le
 * préfixe `u/<uid>/profile/` lisible aurait été une **énumération d'`uid`**.
 * `404 avatar_absent` quand la personne n'en a pas.
 */
export const getAvatarUrl = (uid: string) =>
  call<{ url: string; expiresIn: number; avatarRev: number }>(`/users/${uid}/avatar/url`);

/**
 * `POST /groups/{gid}/members { email }` — **`owner` seul**.
 *
 * L'adresse est le seul identifiant qu'un humain connaisse de quelqu'un d'autre :
 * le `uid` est opaque par construction (D13). Elle est convertie en `uid` par le
 * serveur et **n'est jamais stockée** (I-7) — un listing de membres qui rendrait
 * des adresses ferait de toute appartenance un annuaire.
 *
 * `404 user_unknown` si le compte n'existe pas, et **jamais** une création
 * implicite : l'identité naît d'un acte d'admin et d'aucun autre (D34).
 */
export const addMember = (gid: string, email: string) =>
  call<{ gid: string; uid: string; role: string }>(`/groups/${gid}/members`, {
    method: 'POST',
    body: { email },
  });

export const removeMember = (gid: string, uid: string) =>
  call<{ gid: string; uid: string; removed: true }>(`/groups/${gid}/members/${uid}`, {
    method: 'DELETE',
  });

/** **D16** — sortir sans dépendre de la bonne volonté du propriétaire. */
export const leaveGroup = (gid: string) =>
  call<{ gid: string; uid: string; removed: true }>(`/groups/${gid}/leave`, { method: 'DELETE' });

/**
 * `GET /groups/{gid}/feed` — **les nouveautés du groupe**, et elles sont
 * affichables **sans aucune autre lecture** : chaque arête porte titre, état,
 * `Variants`, `Excerpt` et `RxnAgg` (D28). C'est la propriété qui fait exister cet
 * écran ; sans elle, un fil de 300 lignes demandait 300 appels unitaires.
 *
 * Ce que la page rend n'est **pas** une entité : `RxnAgg` y remplace `reactions`,
 * et ni `Folder` ni `authGroups` n'y figurent — ils n'ont jamais été projetés.
 */
export interface FeedItem {
  readonly Fid?: string;
  readonly Aid?: string;
  readonly Type: string;
  readonly Kind?: string;
  readonly Name: string;
  readonly State?: string;
  readonly Owner: string;
  /** L'auteur, **nommé** : `Owner` seul est un ULID, et « quelqu'un a ajouté une photo » n'est pas un fil. */
  readonly ownerAlias: string | null;
  readonly Excerpt?: string;
  readonly CreatedAt?: string;
  readonly DocumentDate?: string;
  readonly EdgeUpdatedAt?: string;
  readonly Deleted?: boolean;
  readonly Variants?: Record<string, Variant>;
  /** L'affiche choisie par le propriétaire, **projetée** (E3) : le fil montre sa case. */
  readonly PosterFrame?: number | null;
  readonly RxnAgg?: { Emoji: Record<string, number>; ScoreSum: number; ScoreCount: number };
}

/**
 * **Ouvrir une ligne du fil d'un groupe.**
 *
 * Une arête n'est pas une entité, et la différence n'est pas cosmétique : sa
 * projection (D28) porte ce qu'il faut pour *peindre* la ligne — nom, état,
 * `Variants`, `Excerpt`, `PosterFrame` — et rien de ce qui n'a jamais été projeté,
 * `Folder` et `authGroups` en tête. Ce que l'on ouvre est donc **exactement la
 * projection**, remise à la forme d'entité, sans aucune relecture.
 *
 * C'est ce qui rend le geste possible sans requête : la visionneuse et le lecteur
 * ne demandent que `Fid`, `Type`, `Name`, `Variants` et une date, tous projetés.
 * Les URL d'octets, elles, se résolvent comme partout ailleurs — le serveur sonde
 * les groupes de l'appelant pour un `fid` qu'il ne possède pas, c'est la même
 * mécanique que la bande de nouveautés utilise depuis 4.6.
 *
 * Les deux dates ne sont pas interchangeables et le repli suit ce que la ligne
 * affiche déjà : `EdgeUpdatedAt` est *quand ça m'est arrivé*, la seule date qu'une
 * arête garantisse.
 */
export function entityOfFeedItem(item: FeedItem): Entity {
  const when = item.CreatedAt ?? item.EdgeUpdatedAt ?? '';
  return {
    ...(item.Fid !== undefined ? { Fid: item.Fid } : {}),
    ...(item.Aid !== undefined ? { Aid: item.Aid } : {}),
    Type: item.Type,
    ...(item.Kind !== undefined ? { Kind: item.Kind } : {}),
    Name: item.Name,
    ...(item.State !== undefined ? { State: item.State } : {}),
    ...(item.Owner !== undefined ? { Owner: item.Owner } : {}),
    ...(item.Excerpt !== undefined ? { Excerpt: item.Excerpt } : {}),
    ...(item.Variants !== undefined ? { Variants: item.Variants } : {}),
    ...(item.PosterFrame !== undefined ? { PosterFrame: item.PosterFrame } : {}),
    ...(item.DocumentDate !== undefined ? { DocumentDate: item.DocumentDate } : {}),
    CreatedAt: when,
    UpdatedAt: item.EdgeUpdatedAt ?? when,
  };
}

export const getGroupFeed = (gid: string, after?: string, limit = 100) =>
  call<{ items: FeedItem[]; cursor: string | null }>(
    `/groups/${gid}/feed?limit=${limit}${after ? `&after=${encodeURIComponent(after)}` : ''}`,
  );

/**
 * `GET /files/{fid}/shares` — **« où va ma donnée »** (D15), au propriétaire seul.
 *
 * La question se pose toujours à propos d'une chose, jamais dans l'abstrait : c'est
 * pourquoi elle vit dans l'inspecteur et non dans une liste globale, qui aurait
 * redérivé la même donnée sans offrir où agir.
 *
 * `history` est le journal d'audit des expositions — *qui a exposé quoi, à qui,
 * quand* —, que `UpdatedBy` ne dit pas : il ne nomme que le dernier écrivain.
 */
export interface Share {
  readonly gid: string;
  /** Les **raisons** de l'accès : `direct`, ou `ALB#<aid>` pour chaque collection. */
  readonly via: readonly string[];
  readonly since: string;
}

export interface ShareEvent {
  readonly action: string;
  readonly at: string;
  readonly by: string;
  readonly target: string;
  readonly detail?: string;
}

export const getShares = (fid: string) =>
  call<{
    shares: Share[];
    excludedGroups: string[];
    history: ShareEvent[];
    historyNext: string | null;
  }>(`/files/${fid}/shares`);

/**
 * `DELETE /files/{fid}/groups/{gid}` — **le retrait souverain** (D15).
 *
 * Il coupe l'accès **quelle qu'en soit la raison** et inscrit le groupe en
 * exclusion. Sans cette inscription le retrait serait cosmétique : la propagation
 * suivante de n'importe quelle collection partagée à ce groupe le rétablirait.
 *
 * `unblock` lève l'exclusion **sans repartager** — rendre le droit demande un acte
 * de partage neuf, jamais la levée d'un blocage.
 */
export const removeFromGroup = (fid: string, gid: string, unblock = false) =>
  call<{ fid: string; gid: string }>(
    `/files/${fid}/groups/${gid}${unblock ? '?unblock=true' : ''}`,
    { method: 'DELETE' },
  );

/**
 * `GET /files/{fid}/reactions` — **qui a réagi**, au propriétaire seul.
 *
 * Sans elle, la modération était inatteignable : l'agrégat est anonyme, donc aucun
 * client ne pouvait former le `uid` que `DELETE …/reactions/{uid}` exige.
 */
export interface Reactor {
  readonly uid: string;
  readonly alias: string | null;
  readonly emoji: string | null;
  readonly score: number | null;
}

export const listReactions = (fid: string) => call<{ items: Reactor[] }>(`/files/${fid}/reactions`);

export const moderateReaction = (fid: string, uid: string) =>
  call<Entity>(`/files/${fid}/reactions/${uid}`, { method: 'DELETE' });

export const getFolders = () => call<{ items: Folder[] }>('/folders');

export const createFolder = (folder: string) =>
  call<Folder>('/folders', { method: 'POST', body: { folder } });

export const deleteFolder = (folder: string) =>
  call<{ folder: string; deleted: true }>('/folders', { method: 'DELETE', body: { folder } });

/**
 * `GET /files`. Avec `folder`, le listing est servi par GSI3 — le contenu d'un
 * dossier, par ordre alphabétique. Sans, c'est GSI1 : par type et du plus récent
 * au plus ancien. **Ni l'un ni l'autre ne rend de collection** : elle n'est pas
 * rangée, et `GET /albums` la sert.
 */
export function listFiles(query: { folder?: string; type?: string; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (query.folder !== undefined) params.set('folder', query.folder);
  if (query.type) params.set('type', query.type);
  if (query.limit) params.set('limit', String(query.limit));
  const qs = params.toString();
  return call<Page<Entity>>(`/files${qs ? `?${qs}` : ''}`);
}

/**
 * Une URL présignée d'envoi, **avec ses en-têtes** : ils sont signés, et les
 * renvoyer tels quels n'est pas une convention mais une obligation (I-21). Un
 * en-tête altéré, ajouté ou omis fait répondre `SignatureDoesNotMatch`.
 */
export interface Upload {
  readonly url: string;
  readonly headers: Record<string, string>;
  readonly expiresIn: number;
}

/** Le `fid` est **fourni par le client** (D14) : un rejeu ne duplique rien. */
export function createFile(body: {
  fid: string;
  type: string;
  kind?: string;
  name: string;
  folder?: string;
  roles?: { role: string; ext: string; mime: string }[];
  /** L'empreinte de la source *(K1)* — écrite telle quelle, jamais vérifiée. */
  sha256?: string;
}) {
  return call<Entity & { uploads?: Record<string, Upload> }>('/files', {
    method: 'POST',
    body,
  });
}

/**
 * `POST /files/{fid}/uploads` — **redélivrer** des URL d'envoi (D19).
 *
 * Mêmes clés, mêmes extensions, **mêmes en-têtes signés** que la création : un
 * rôle partiellement envoyé se réécrit sans dupliquer d'octets. C'est le pont
 * entre deux durées volontairement distinctes — **une URL présignée meurt en
 * 900 s, l'entité vit 30 jours** (api.md §3).
 *
 * `409 already_complete` sur une entité `PROCESSING` ou `READY` : la reprise n'a
 * alors plus d'objet, et l'erreur vaut mieux qu'une URL vers un objet qu'on
 * réécrirait.
 */
export const redeliverUploads = (fid: string, roles: string[]) =>
  call<{ uploads: Record<string, Upload>; expiresAt: string }>(`/files/${fid}/uploads`, {
    method: 'POST',
    body: { roles },
  });

export const deleteFile = (fid: string) =>
  call<{ fid: string; deleted: true }>(`/files/${fid}`, { method: 'DELETE' });

/**
 * **La corbeille** (`api.md` §7) — entités **et** partages, déjà triés par date
 * de purge, du plus récemment supprimé au plus ancien.
 *
 * Elle ne se déduit d'aucune autre liste : un objet mis en corbeille a **quitté**
 * les index de listing au même instant, il n'est donc nulle part ailleurs. Et
 * elle ne s'ajoute pas au miroir local : ce qu'on y trouve n'existe plus du point
 * de vue de tous les autres écrans, et l'y mêler ferait réapparaître des objets
 * supprimés dans la galerie au premier drainage.
 */
export const listTrash = (limit = 200, after?: string) =>
  call<Page<Entity & { readonly purgeAt: string | null }>>(
    `/trash?limit=${limit}${after ? `&after=${encodeURIComponent(after)}` : ''}`,
  );

/**
 * `POST /files/{fid}/restore` — le symétrique exact de la suppression. Elle rend
 * l'entité **et** les accès que la mise en corbeille avait couchés ; un retrait
 * délibéré, lui, n'est jamais défait.
 *
 * **`404 purged`** au-delà des trente jours : il ne reste alors qu'un tombstone
 * sans octets, et restaurer un objet vide n'a pas de sens.
 */
export const restoreFile = (fid: string) =>
  call<Entity>(`/files/${fid}/restore`, { method: 'POST' });

/**
 * `POST /files/{fid}/describe` — **« Régénérer »** *(R6)*. `202` : la description
 * est enfilée, et revient par la synchronisation comme toute mutation de l'entité.
 * Elle **écrase** ce qui a été corrigé à la main — la confirmation est à l'écran.
 */
export const describeFile = (fid: string) =>
  call<{ fid: string; queued: boolean }>(`/files/${fid}/describe`, { method: 'POST' });

/**
 * `POST /albums/{aid}/restore` — et **restaurer re-partage** (D31) : l'audience
 * que le propriétaire avait posée est rétablie, telle quelle. C'est un choix, pas
 * une conséquence : sans lui, une suppression accidentelle serait une perte de
 * configuration irrattrapable.
 *
 * La réponse arrive avec `shareState: "RESTORING"` — le contenu revient derrière,
 * par une cascade paginée.
 */
export const restoreAlbum = (aid: string) =>
  call<Entity & { readonly shareState?: string }>(`/albums/${aid}/restore`, { method: 'POST' });

/**
 * `PATCH /files/{fid}` — **un corps plat**, et c'est le code qui fait foi : la
 * forme par `ops` décrite en §3 d'`api.md` n'a jamais été implémentée (la table
 * des divergences le consigne). Chaque champ présent est une op ; aucun champ
 * présent rend `400 no_op`.
 *
 * **Il n'y a ni fusion, ni `If-Match`, ni 412** : deux appareils qui renomment
 * et retaguent en même temps convergent sans arbitrage.
 *
 * `camera` et `gps` n'y figurent pas, et c'est délibéré : ce sont des faits de
 * prise de vue, écrits une fois. Les rendre modifiables ouvrirait la
 * falsification d'une provenance pour zéro usage.
 */
export const patchFile = (
  fid: string,
  body: {
    name?: string;
    folder?: string;
    description?: string | null;
    documentDate?: string;
    /**
     * L'affiche d'une vidéo (M6) — un entier borné par `Variants.thumb.frames`,
     * ou `null` pour revenir à la première case. Le serveur refuse une valeur
     * hors planche (`400 poster_frame_invalid`, qui rend `frames`) plutôt que de
     * la ramener dedans : à l'écriture, une case qui n'existe pas est une erreur.
     */
    posterFrame?: number | null;
    /**
     * **Les mots-clés, en remplacement complet** *(R6)* — idempotent, et une
     * liste vide les retire. Écrire l'un des deux champs décrits le reprend à la
     * description automatique.
     */
    tags?: readonly string[];
  },
) => call<Entity>(`/files/${fid}`, { method: 'PATCH', body });

export const moveFile = (fid: string, folder: string) => patchFile(fid, { folder });

export const getFile = (fid: string) => call<Entity>(`/files/${fid}`);

export const completeFile = (fid: string, roles: string[]) =>
  call<Entity>(`/files/${fid}/complete`, { method: 'POST', body: { roles } });

/**
 * `GET /files/{fid}/url?role=` — une URL de lecture, valable quelques minutes.
 *
 * `409 bytes_not_ready` n'est **pas** une panne : c'est la réponse juste tant que
 * le worker n'a pas produit le dérivé. Le drapeau est `Variants.<role>.size`, et
 * l'appelant a de quoi réessayer plutôt que d'afficher une image cassée.
 */
export const getFileUrl = (fid: string, role: string) =>
  call<{ url: string; role: string; expiresIn: number }>(
    `/files/${fid}/url?role=${encodeURIComponent(role)}`,
  );

/**
 * `POST /files/urls` — **la présignature en lot** (D-22).
 *
 * Une page de photothèque = **un** appel. La route unitaire reste le chemin d'une
 * image ouverte seule ; elle ne tient pas l'échelle d'une grille, où deux cents
 * vignettes faisaient deux cents allers-retours contre un stage étranglé à 20 req/s.
 *
 * **`omitted` n'est pas une erreur**, et le confondre avec une panne serait le
 * défaut à ne pas commettre : `bytes_not_ready` veut dire *réessaie plus tard*
 * (le worker n'a pas fini), `unreadable` veut dire *n'insiste pas*, et
 * `role_unknown` veut dire *ce type n'a pas ce rôle*. Une seule de ces trois
 * situations mérite une nouvelle demande.
 *
 * Le `gid` est **un indice de résolution**, jamais un droit : il épargne au serveur
 * de sonder les groupes de l'appelant pour un `fid` qu'il ne possède pas. La
 * décision reste portée par l'arête.
 */
export interface UrlItem {
  readonly fid: string;
  readonly role: string;
  readonly url: string;
  readonly expiresIn: number;
  readonly size: number;
  readonly mime: string;
}

export interface OmittedUrl {
  readonly fid: string;
  readonly role: string;
  readonly reason: 'unreadable' | 'bytes_not_ready' | 'role_unknown';
}

export const batchUrls = (items: readonly { fid: string; role: string; gid?: string }[]) =>
  call<{ items: UrlItem[]; omitted: OmittedUrl[] }>('/files/urls', {
    method: 'POST',
    body: { items },
  });

/**
 * **Réagir** (D24) — `PUT /files/{fid}/reaction`.
 *
 * Les deux champs sont optionnels **séparément**, et c'est la même écriture qui
 * pose un emoji, une note, ou les deux. L'écriture est atomique sur `Reactions.#uid`
 * : aucune fusion, donc aucun écrasement possible de la réaction d'un autre.
 *
 * Réagir est ouvert à **quiconque peut lire**, propriétaire ou non — capacité
 * structurellement impossible en V1.
 *
 * **L'écriture remplace l'entrée entière** — `SET Reactions.<uid> = <valeur>`, pas
 * une fusion. Envoyer `{ score }` seul **efface l'emoji** de la même personne. Un
 * appelant qui veut changer l'un sans perdre l'autre doit donc renvoyer les deux,
 * et c'est à l'appelant de le faire : le serveur ne peut pas deviner qu'une absence
 * veut dire « garde ce qui y était » plutôt que « retire-le ».
 *
 * Les deux routes rendent **l'entité sérialisée**, réactions comprises : c'est elle
 * qui fait autorité, et la recopier vaut mieux que reconstruire l'état localement.
 */
export const putReaction = (fid: string, body: { emoji?: string; score?: number }) =>
  call<Entity>(`/files/${fid}/reaction`, { method: 'PUT', body });

/** `DELETE /files/{fid}/reaction` — retire **l'entrée entière**, emoji et note. */
export const deleteReaction = (fid: string) =>
  call<Entity>(`/files/${fid}/reaction`, { method: 'DELETE' });

/**
 * Les **collections**. Une collection est une entité (§5.1) — elle porte `Aid` là
 * où une entité porte `Fid` — mais elle n'est **pas rangée** : ni `Folder`, ni
 * place dans l'arborescence. Elle est un *ensemble* N-N, et la seule chose que le
 * socle sache exposer à un groupe. Le nom de la route reste `/albums` : le
 * contrat est lu par deux clients et dix-sept fichiers E2E, et un libellé
 * français sur un identifiant anglais est déjà la norme du dépôt.
 */
export interface AlbumItemResult {
  readonly fid: string;
  readonly status: 'added' | 'already' | 'denied';
  readonly code?: string;
  readonly edgeUpdatedAt?: string;
}

export const listAlbums = () => call<Page<Entity>>('/albums');

/**
 * L'`aid` est fourni par le client (D14), comme le `fid`. **Pas de `folder`** :
 * le serveur rend `400 folder_unsupported`.
 */
export const createAlbum = (body: { aid: string; name: string; contribPolicy?: string }) =>
  call<{ aid: string }>('/albums', { method: 'POST', body });

export const getAlbum = (aid: string) => call<Entity>(`/albums/${aid}`);

/**
 * `PATCH /albums/{aid}` — renommer, ou changer qui peut y verser.
 *
 * **Pas de `folder`** : le serveur rend `400 folder_unsupported`. Une collection
 * n'est pas rangée, c'est un ensemble et non un emplacement.
 */
export const patchAlbum = (aid: string, body: { name?: string; contribPolicy?: string }) =>
  call<Entity>(`/albums/${aid}`, { method: 'PATCH', body });

/**
 * `DELETE /albums/{aid}` — **un tombstone, jamais une suppression dure**, et la
 * cascade retire les arêtes d'accès : la collection disparaît de tous les groupes
 * où elle était partagée.
 *
 * Ce qu'elle contenait n'est **pas** supprimé. C'est la même règle que « retirer
 * n'est pas supprimer », appliquée à l'ensemble entier — et c'est ce que l'écran
 * doit dire avant de le faire.
 */
export const deleteAlbum = (aid: string) =>
  call<{ aid: string; deleted: boolean }>(`/albums/${aid}`, { method: 'DELETE' });

/**
 * Ce qu'une page de partage rend : une **arête**, plus le nom de qui a versé.
 *
 * Elle n'est pas une entité — la projection ne porte ni `Folder`, ni `authGroups`,
 * ni `albums` (D28, I-27) —, et le type le dit en héritant d'`Entity` dont tout
 * est optionnel. `ownerAlias` est la seule chose que la route ajoute aux octets de
 * l'arête, et elle est là pour une raison : `Owner` est un ULID opaque, et
 * « isoler mes éléments ou ceux d'un autre » ne se demande pas à des
 * identifiants *(R3, 2026-09-12)*.
 */
export interface AlbumItem extends Entity {
  /** `null` quand la personne n'a pas choisi d'alias — jamais son `uid` déguisé en nom. */
  readonly ownerAlias?: string | null;
  /**
   * **L'empreinte visuelle de la cible** *(DB12, 2026-09-20)* — servie **seulement
   * quand on la demande** (`?hashes=1`), c'est-à-dire en mode doublons.
   *
   * `lowerCamel` : c'est une **vue** calculée au point de lecture, comme
   * `ownerAlias`, et non l'attribut `VisualHash` de l'item — il n'est pas projeté
   * sur les arêtes, et ne le sera pas. Absente d'une vidéo, d'une photo d'avant
   * K3 sans rattrapage, et de toute ligne quand on ne l'a pas demandée.
   */
  readonly visualHash?: string;
}

export const listAlbumItems = (aid: string, limit = 200, hashes = false) =>
  call<Page<AlbumItem>>(`/albums/${aid}/items?limit=${limit}${hashes ? '&hashes=1' : ''}`);

/**
 * `POST /albums/{aid}/items` — **un rapport par fid**, jamais un succès global.
 *
 * Une transaction unique aurait annulé l'ajout des 99 autres dès qu'un seul était
 * déjà présent ; la route exécute donc une transaction par élément et rend l'état
 * de chacun. Un `already` est un **succès** : l'état voulu est atteint.
 */
export const addAlbumItems = (aid: string, fids: readonly string[]) =>
  call<{ results: AlbumItemResult[] }>(`/albums/${aid}/items`, {
    method: 'POST',
    body: { items: fids.map((fid) => ({ fid })) },
  });

/**
 * `POST /albums/{aid}/groups` — **le seul partage positif du socle**. Il n'existe
 * aucune route pour exposer une entité seule : c'est ce qui fait de la collection
 * l'unité de partage, et non un choix d'interface.
 *
 * **`202`** : l'arête d'accès et `AuthGroups` sont écrits synchroniquement — la
 * collection apparaît aussitôt dans le groupe — mais la propagation aux éléments
 * qu'elle contient est asynchrone.
 */
export const shareAlbum = (aid: string, add: readonly string[], remove: readonly string[] = []) =>
  call<{ aid: string; shareState: string }>(`/albums/${aid}/groups`, {
    method: 'POST',
    body: { add, remove },
  });

/** Retirer d'une collection **ne supprime rien** — la phrase est dans l'écran. */
export const removeAlbumItem = (aid: string, fid: string) =>
  call<{ aid: string; fid: string; removed: boolean }>(`/albums/${aid}/items/${fid}`, {
    method: 'DELETE',
  });

/**
 * `POST /files/{fid}/content { baseRev }` — **prendre le verrou** et recevoir
 * l'URL d'écriture. Le `rev` n'est jamais proposé par le client : le serveur le
 * dérive du `baseRev` constaté et le range dans le verrou.
 *
 * Trois refus possibles, et ce sont **trois situations distinctes** :
 * `409 stale_rev` (quelqu'un a enregistré depuis, il faut relire),
 * `409 locked` (quelqu'un édite en ce moment, `expiresAt` dit jusqu'à quand),
 * et l'absence de droit, qui est un `403` — le partage est en lecture.
 */
export interface ContentLease {
  readonly fid: string;
  readonly rev: number;
  readonly token: string;
  readonly upload: Upload;
}

export const startContent = (fid: string, baseRev: number) =>
  call<ContentLease>(`/files/${fid}/content`, { method: 'POST', body: { baseRev } });

/**
 * `POST /files/{fid}/content/complete { token, versionId }`.
 *
 * Le `versionId` **n'est pas décoratif** : le commit est pinné dessus, et c'est
 * lui — non la fenêtre de présignature — qui rend inoffensif un `PUT` arrivé en
 * retard. Il se lit dans l'en-tête `x-amz-version-id` de la réponse S3, que le
 * CORS du bucket expose explicitement.
 */
export const completeContent = (fid: string, token: string, versionId: string) =>
  call<{
    fid: string;
    rev: number;
    contentVersionId: string;
    size: number;
    updatedAt: string;
    /**
     * L'extrait recalculé.
     *
     * **Trois états, et ils veulent dire trois choses** : une chaîne, c'est le
     * nouvel extrait ; `null`, c'est « la note est vide », donc l'attribut
     * disparaît ; **absent**, c'est un serveur qui ne connaît pas encore ce
     * champ, et il ne faut alors surtout rien changer. Le troisième n'est pas
     * théorique — le paquet Lambda et le bundle web ne sont pas déployés au même
     * instant, et un client neuf parle à l'ancien serveur pendant la bascule.
     */
    excerpt?: string | null;
  }>(`/files/${fid}/content/complete`, { method: 'POST', body: { token, versionId } });

/** Une révision de contenu, avec **l'instantané des métadonnées** de l'époque. */
export interface Revision {
  readonly rev: number;
  readonly at: string;
  readonly by: string;
  readonly size: number;
  readonly name: string;
  readonly folder: string | null;
  readonly description: string | null;
  readonly tags: readonly string[];
  readonly documentDate: string | null;
}

/**
 * `GET /files/{fid}/versions` — **au propriétaire seul** (D22). Sur un contenu
 * reçu, l'appel rendrait `403` : l'interface ne doit donc pas le proposer, et
 * n'affiche pas un bouton grisé mais **aucun bouton**.
 */
export const listVersions = (fid: string, limit = 50) =>
  call<Page<Revision>>(`/files/${fid}/versions?limit=${limit}`);

/** `GET /files/{fid}/versions/{rev}/url` — les octets **de cette révision-là**. */
export const versionUrl = (fid: string, rev: number) =>
  call<{ rev: number; url: string; expiresIn: number; size: number; mime: string }>(
    `/files/${fid}/versions/${rev}/url`,
  );

/** Le curseur de `/changes` est **opaque** : jamais interprété, seulement rendu. */
export interface Changes {
  readonly items: Record<string, unknown>[];
  readonly cursor: string;
  readonly full: boolean;
  readonly hasMore: boolean;
}

export const getChanges = (cursor?: string) =>
  call<Changes>(`/changes${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);

// ─────────────────────────────────────────────────────────────────────────────
// Administration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * **Ce qu'un admin voit d'un compte.** Jamais l'adresse : elle n'est stockée sur
 * aucun item (I-7), et la console n'a donc rien à en montrer — c'est l'alias qui
 * nomme les gens, ici comme dans un cercle.
 */
export interface AdminUser {
  readonly uid: string;
  readonly alias: string | null;
  readonly role: 'user' | 'admin';
  readonly status: 'active' | 'disabled';
  readonly createdAt: string | null;
  readonly entities: number;
  readonly albums: number;
  readonly groups: number;
  readonly folders: number;
  readonly bytes: number;
  /** `false` = la création s'est arrêtée entre la table et Cognito : espace inerte. */
  readonly linked: boolean;
}

export const listAdminUsers = (cursor?: string, limit = 50) =>
  call<{ items: AdminUser[]; cursor: string | null }>(
    `/admin/users?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );

export const createAdminUser = (email: string, alias?: string, role: 'user' | 'admin' = 'user') =>
  call<{ uid: string }>('/admin/users', { method: 'POST', body: { email, alias, role } });

export const setAdminUserStatus = (uid: string, status: 'active' | 'disabled') =>
  call<{ uid: string; status: string }>(`/admin/users/${uid}`, {
    method: 'PATCH',
    body: { status },
  });

/**
 * **Renvoyer quelqu'un vers son mot de passe.** Le `mode` n'est pas un détail de
 * mise en œuvre : `invitation` annonce un mot de passe provisoire dans la boîte,
 * `reset` annonce un code. Le serveur le décide sur l'état du compte Cognito, que
 * l'écran ne connaît pas.
 */
export const resetAdminUserPassword = (uid: string) =>
  call<{ uid: string; mode: 'invitation' | 'reset' }>(`/admin/users/${uid}/password-reset`, {
    method: 'POST',
    body: {},
  });

/** Une mesure technique **et son âge** : sans l'âge, on la lirait comme un instantané. */
export interface Mesure {
  readonly value: number | null;
  readonly at: string | null;
}

export interface AdminStats {
  readonly business: {
    readonly users: number;
    readonly active: number;
    readonly disabled: number;
    readonly admins: number;
    readonly incomplete: number;
    readonly entities: number;
    readonly albums: number;
    readonly groups: number;
    readonly folders: number;
    readonly bytes: number;
  };
  readonly technical: {
    readonly tableItems: Mesure;
    readonly tableBytes: Mesure;
    readonly bucketBytes: Mesure;
    readonly bucketObjects: Mesure;
    readonly queueDepth: number | null;
    readonly queueInFlight: number | null;
    readonly cognitoUsers: number | null;
  };
  readonly at: string;
}

export const getAdminStats = () => call<AdminStats>('/admin/stats');

/**
 * `POST /admin/describe` — **le rattrapage des descriptions** *(R5)*. Il **enfile**
 * les photos et vidéos `READY` jamais décrites (ou, avec `upgrade`, décrites sous un
 * prompt plus ancien) ; `dryRun` compte sans rien enfiler. La route rend la main
 * au bout d'une vingtaine de secondes avec un `next`, que l'écran repasse.
 */
export interface DescribeBacklog {
  readonly users: number;
  readonly scanned: number;
  readonly selected: number;
  readonly enqueued: number;
  readonly prompt: string;
  readonly next: string | null;
}

export const describeBacklog = (body: { upgrade?: boolean; dryRun?: boolean; cursor?: string }) =>
  call<DescribeBacklog>('/admin/describe', { method: 'POST', body });

/**
 * `POST /admin/visual-hash` — **le rattrapage des empreintes visuelles** *(K3)*. Il
 * enfile un job « empreinte seule » par photo `READY` qui n'en a pas ; `dryRun`
 * compte sans rien enfiler. Même pagination que les descriptions.
 */
export interface VisualHashBacklog {
  readonly users: number;
  readonly scanned: number;
  readonly selected: number;
  readonly enqueued: number;
  readonly next: string | null;
}

export const visualHashBacklog = (body: { dryRun?: boolean; cursor?: string }) =>
  call<VisualHashBacklog>('/admin/visual-hash', { method: 'POST', body });
